/**
 * Branding Page — Admin Console.
 *
 * Wave 1c W1c-07 — Tenant-admin editor wired to real endpoints.
 *
 * Replaces the legacy FileReader/base64 path with:
 *   - real multipart uploads via POST /branding/assets (W1c-03)
 *   - asset-id references (UUIDs), not data URLs
 *   - explicit Save button → PUT /branding (W1c-06)
 *   - Reset → revert draft to last successful save
 *   - dirty-state nav guard (beforeunload)
 *   - locked fields render disabled with a lock badge tooltip (Q3)
 *   - WCAG contrast warning under the affected color picker (Q5);
 *     saving with low contrast pops a confirm + records acknowledgment
 *   - live preview reads from local draft state (synthetic mock — Q8)
 *
 * Hot-reload (W1c-08) is provided by BrandingProvider's BroadcastChannel.
 * After a successful save we call `updateBranding(effective)` which
 * broadcasts to peer tabs.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  Palette,
  Upload,
  Save,
  Undo2,
  Layout,
  Image as ImageIcon,
  X,
  User,
  Lock,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useBranding } from '@/contexts/branding-context';
import {
  useBrandingQuery,
  useUpdateBranding,
  useUploadBrandingAsset,
  useDeleteBrandingAsset,
  type BrandingColors,
  type BrandingDisplayMode,
  type EffectiveBranding,
  type BrandingAssetKind,
  type BrandingError,
} from '@/lib/use-branding';
import { SYSTEM_DEFAULT_BRANDING } from '@/lib/branding-defaults';
import { toast } from 'sonner';

export const Route = createFileRoute('/admin/branding')({ component: AdminBrandingPage });

// ─── Local draft model ──────────────────────────────────────────────────────

/**
 * Draft kept by the editor while the user is making changes. Asset slots
 * carry both the *id* (what gets PUT to the server) and the *previewUrl*
 * (what the live preview uses to render the bitmap). The previewUrl is
 * uncommitted state — never sent to the server.
 *
 * `previousAssetIds` accumulates the asset ids that this editor session
 * uploaded a replacement for, so we can DELETE them after a successful
 * save (best-effort cleanup; the server's 30-day grace also catches them).
 */
interface DraftAssetSlot {
  assetId: string | null;
  previewUrl: string | null;
}

interface BrandingDraft {
  useDefaultTheme: boolean;
  displayMode: BrandingDisplayMode;
  portalTitle: string;
  logoFull: DraftAssetSlot;
  logoIcon: DraftAssetSlot;
  favicon: DraftAssetSlot;
  loginBackground: DraftAssetSlot;
  loginWelcomeText: string | null;
  loginSubtitle: string | null;
  footerText: string | null;
  light: BrandingColors;
  dark: BrandingColors;
}

interface AssetSlotMeta {
  field: keyof BrandingDraft;
  apiKind: BrandingAssetKind;
  writeField:
    | 'logoFullAssetId'
    | 'logoIconAssetId'
    | 'faviconAssetId'
    | 'loginBackgroundAssetId';
  lockedFieldName:
    | 'logoFullAssetId'
    | 'logoIconAssetId'
    | 'faviconAssetId'
    | 'loginBackgroundAssetId';
}

const ASSET_SLOTS: Record<'logoFull' | 'logoIcon' | 'favicon' | 'loginBackground', AssetSlotMeta> = {
  logoFull: {
    field: 'logoFull',
    apiKind: 'logo_full',
    writeField: 'logoFullAssetId',
    lockedFieldName: 'logoFullAssetId',
  },
  logoIcon: {
    field: 'logoIcon',
    apiKind: 'logo_icon',
    writeField: 'logoIconAssetId',
    lockedFieldName: 'logoIconAssetId',
  },
  favicon: {
    field: 'favicon',
    apiKind: 'favicon',
    writeField: 'faviconAssetId',
    lockedFieldName: 'faviconAssetId',
  },
  loginBackground: {
    field: 'loginBackground',
    apiKind: 'login_background',
    writeField: 'loginBackgroundAssetId',
    lockedFieldName: 'loginBackgroundAssetId',
  },
};

// ─── Conversions ────────────────────────────────────────────────────────────

/**
 * Pull the asset id out of an effective URL like
 *   `/api/v1/branding/assets/<uuid>?v=42`.
 * Returns null when the URL doesn't match (e.g., a hard-coded default path
 * such as `/ArgusLogo_500px-Wordmark-TM.png`).
 */
function extractAssetId(url: string | null | undefined): string | null {
  if (!url) return null;
  const match = url.match(/\/api\/v1\/branding\/assets\/([0-9a-fA-F-]{36})/);
  return match ? match[1] : null;
}

function effectiveToDraft(eff: EffectiveBranding): BrandingDraft {
  return {
    useDefaultTheme: eff.useDefaultTheme,
    displayMode: eff.displayMode,
    portalTitle: eff.portalTitle,
    logoFull: { assetId: extractAssetId(eff.logoUrl), previewUrl: eff.logoUrl ?? null },
    logoIcon: { assetId: extractAssetId(eff.logoIconUrl), previewUrl: eff.logoIconUrl ?? null },
    favicon: { assetId: extractAssetId(eff.faviconUrl), previewUrl: eff.faviconUrl ?? null },
    loginBackground: {
      assetId: extractAssetId(eff.loginBackgroundUrl),
      previewUrl: eff.loginBackgroundUrl ?? null,
    },
    loginWelcomeText: eff.loginWelcomeText,
    loginSubtitle: eff.loginSubtitle,
    footerText: eff.footerText,
    light: eff.light,
    dark: eff.dark,
  };
}

function defaultDraft(): BrandingDraft {
  return effectiveToDraft({
    ...SYSTEM_DEFAULT_BRANDING,
    effectiveVersion: 0,
    lockedFields: [],
    source: 'system_default',
  } as unknown as EffectiveBranding);
}

// ─── WCAG contrast (pure helper) ────────────────────────────────────────────

/**
 * Returns the WCAG 2.1 contrast ratio (1..21) between two hex colors.
 * Mirrors the server-side calculation in api/utils/wcag-contrast.ts so
 * the UI shows the same number the server records in the audit row.
 */
function wcagContrast(fg: string, bg: string): number {
  const lum = (hex: string): number => {
    const m = hex.match(/^#([0-9a-fA-F]{6})$/);
    if (!m) return 1;
    const n = parseInt(m[1], 16);
    const r = (n >> 16) & 0xff;
    const g = (n >> 8) & 0xff;
    const b = n & 0xff;
    const ch = (c: number) => {
      const s = c / 255;
      return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
  };
  const L1 = lum(fg);
  const L2 = lum(bg);
  const [hi, lo] = L1 >= L2 ? [L1, L2] : [L2, L1];
  return (hi + 0.05) / (lo + 0.05);
}

const WCAG_AA_NORMAL = 4.5;

// ─── Color Picker (with optional lock badge) ────────────────────────────────

function ColorPicker({
  label,
  value,
  onChange,
  locked,
  warning,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  locked?: boolean;
  warning?: string;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-medium">{label}</Label>
        {locked && <LockBadge />}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          disabled={locked}
          onChange={(e) => onChange(e.target.value)}
          aria-label={`${label} color value`}
          className="h-8 w-8 shrink-0 cursor-pointer rounded border border-border bg-transparent p-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
        />
        <Input
          value={value}
          disabled={locked}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          aria-label={`${label} hex value`}
          className="font-mono text-xs h-8"
        />
      </div>
      {warning && (
        <p className="flex items-start gap-1 text-[11px] text-amber-700 dark:text-amber-400">
          <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
          <span>{warning}</span>
        </p>
      )}
    </div>
  );
}

function LockBadge() {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground bg-muted/60 px-1.5 py-0.5 rounded">
          <Lock className="h-3 w-3" />
          Locked
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <p className="text-xs max-w-xs">
          Locked by your platform administrator. Contact support to request a change.
        </p>
      </TooltipContent>
    </Tooltip>
  );
}

// ─── Logo Upload Slot — wired to POST /branding/assets ──────────────────────

function LogoUploadSlot({
  label,
  hint,
  aspectClass,
  slot,
  apiKind,
  locked,
  onChange,
  uploadAsset,
}: {
  label: string;
  hint: string;
  aspectClass: string;
  slot: DraftAssetSlot;
  apiKind: BrandingAssetKind;
  locked: boolean;
  onChange: (next: DraftAssetSlot, previousAssetId: string | null) => void;
  uploadAsset: (kind: BrandingAssetKind, file: File) => Promise<{ id: string; url: string }>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const handleFile = useCallback(
    async (file: File) => {
      // Server-side magic-byte sniffer is the source of truth, but we
      // do a quick client-side sanity check to give faster feedback.
      const allowedTypes = [
        'image/png',
        'image/jpeg',
        'image/jpg',
        'image/svg+xml',
        'image/x-icon',
        'image/vnd.microsoft.icon',
      ];
      if (!allowedTypes.includes(file.type) && file.type !== '') {
        toast.error(`Unsupported format (${file.type || 'unknown'}).`);
        return;
      }
      const sizeCap = apiKind === 'login_background' ? 5 * 1024 * 1024 : 2 * 1024 * 1024;
      if (file.size > sizeCap) {
        toast.error(`File must be under ${sizeCap / 1024 / 1024} MB.`);
        return;
      }

      setBusy(true);
      try {
        const result = await uploadAsset(apiKind, file);
        onChange({ assetId: result.id, previewUrl: result.url }, slot.assetId);
        toast.success('Asset uploaded.');
      } catch (err) {
        const e = err as BrandingError;
        toast.error(e.message ?? 'Upload failed.');
      } finally {
        setBusy(false);
      }
    },
    [apiKind, onChange, slot.assetId, uploadAsset]
  );

  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange({ assetId: null, previewUrl: null }, slot.assetId);
    },
    [onChange, slot.assetId]
  );

  const triggerFilePicker = useCallback(() => {
    if (locked || busy) return;
    inputRef.current?.click();
  }, [locked, busy]);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-medium">{label}</Label>
        {locked && <LockBadge />}
      </div>
      <div
        role="button"
        tabIndex={locked ? -1 : 0}
        aria-label={`Upload ${label}`}
        aria-disabled={locked || undefined}
        onKeyDown={(e) => {
          if (locked || busy) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (!slot.previewUrl) triggerFilePicker();
          }
        }}
        className={`relative border-2 border-dashed rounded-lg flex flex-col items-center justify-center transition-colors ${aspectClass} ${
          slot.previewUrl ? 'p-1' : 'p-4'
        } ${
          locked
            ? 'cursor-not-allowed opacity-50 border-border'
            : 'cursor-pointer hover:border-primary/50 border-border'
        }`}
        onClick={() => !slot.previewUrl && triggerFilePicker()}
      >
        {busy ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : slot.previewUrl ? (
          <>
            <img src={slot.previewUrl} alt={label} className="max-h-full max-w-full object-contain" />
            {!locked && (
              <div className="absolute bottom-1 right-1 flex gap-1">
                <Button
                  size="sm"
                  variant="secondary"
                  className="h-6 px-2 text-xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    triggerFilePicker();
                  }}
                  disabled={busy}
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  className="h-6 px-2 text-xs"
                  onClick={handleRemove}
                  aria-label={`Remove ${label}`}
                  disabled={busy}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            )}
          </>
        ) : (
          <>
            <Upload className="h-5 w-5 text-muted-foreground mb-1" />
            <span className="text-xs text-muted-foreground">Click to upload</span>
            <span className="text-[10px] text-muted-foreground/70 mt-0.5">{hint}</span>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/svg+xml,image/x-icon,image/vnd.microsoft.icon"
          className="hidden"
          disabled={locked || busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
            // Reset so re-selecting the same file triggers change
            e.target.value = '';
          }}
        />
      </div>
    </div>
  );
}

// ─── Live Preview ───────────────────────────────────────────────────────────

function LivePreview({
  draft,
  theme,
}: {
  draft: BrandingDraft;
  theme: BrandingColors;
}) {
  const headerLogo = () => {
    switch (draft.displayMode) {
      case 'full':
        return draft.logoFull.previewUrl ? (
          <img src={draft.logoFull.previewUrl} alt="logo" className="h-5 object-contain" />
        ) : (
          <span className="text-xs font-bold" style={{ color: theme.primaryForeground }}>
            {draft.portalTitle}
          </span>
        );
      case 'both':
        return (
          <div className="flex items-center gap-1.5">
            {draft.logoIcon.previewUrl ? (
              <img src={draft.logoIcon.previewUrl} alt="icon" className="h-5 w-5 object-contain" />
            ) : (
              <div
                className="h-5 w-5 rounded"
                style={{ backgroundColor: theme.primaryForeground, opacity: 0.3 }}
              />
            )}
            {draft.logoFull.previewUrl ? (
              <img src={draft.logoFull.previewUrl} alt="logo" className="h-4 object-contain" />
            ) : (
              <span className="text-xs font-bold" style={{ color: theme.primaryForeground }}>
                {draft.portalTitle}
              </span>
            )}
          </div>
        );
      case 'icon-name':
        return (
          <div className="flex items-center gap-1.5">
            {draft.logoIcon.previewUrl ? (
              <img src={draft.logoIcon.previewUrl} alt="icon" className="h-5 w-5 object-contain" />
            ) : (
              <div
                className="h-5 w-5 rounded"
                style={{ backgroundColor: theme.primaryForeground, opacity: 0.3 }}
              />
            )}
            <span className="text-xs font-bold" style={{ color: theme.primaryForeground }}>
              {draft.portalTitle}
            </span>
          </div>
        );
    }
  };

  const sidebarItems = [
    { name: 'Dashboard', active: true },
    { name: 'Tenants', active: false },
    { name: 'Users', active: false, hover: true },
    { name: 'Settings', active: false },
  ];

  return (
    <div
      className="rounded-lg border overflow-hidden text-left"
      style={{ fontSize: '12px' }}
      aria-live="polite"
    >
      <div
        className="h-9 flex items-center justify-between px-3"
        style={{ backgroundColor: theme.primary }}
      >
        {headerLogo()}
        <User className="h-4 w-4" style={{ color: theme.primaryForeground }} />
      </div>

      <div className="flex gap-0 border-b" style={{ backgroundColor: theme.sidebarBackground }}>
        {['Overview', 'Details', 'Users'].map((tab, i) => (
          <div
            key={tab}
            className="px-3 py-1.5 text-[11px] font-medium"
            style={{
              backgroundColor: i === 0 ? theme.sidebarActive : 'transparent',
              color: i === 0 ? theme.sidebarActiveText : theme.sidebarText,
            }}
          >
            {tab}
          </div>
        ))}
      </div>

      <div className="flex" style={{ minHeight: 140 }}>
        <div
          className="w-28 shrink-0 py-2 px-1.5 space-y-0.5"
          style={{ backgroundColor: theme.sidebarBackground }}
        >
          {sidebarItems.map((item) => (
            <div
              key={item.name}
              className="px-2 py-1 rounded text-[11px]"
              style={{
                backgroundColor: item.active
                  ? theme.sidebarActive
                  : item.hover
                    ? theme.sidebarHover
                    : 'transparent',
                color: item.active ? theme.sidebarActiveText : theme.sidebarText,
              }}
            >
              {item.name}
              {item.hover && <span className="text-[9px] opacity-60 ml-1">(Hover)</span>}
            </div>
          ))}
        </div>

        <div className="flex-1 p-3 space-y-2" style={{ backgroundColor: theme.background }}>
          <div className="text-sm font-semibold" style={{ color: theme.text }}>
            Page Title
          </div>
          <button
            className="px-3 py-1 rounded text-[11px] font-medium"
            style={{ backgroundColor: theme.primary, color: theme.primaryForeground }}
          >
            Primary Button
          </button>
          <div className="rounded p-2 mt-1" style={{ backgroundColor: theme.card }}>
            <div className="text-[11px] font-medium" style={{ color: theme.text }}>
              Card Component
            </div>
            <div className="text-[10px] mt-0.5" style={{ color: theme.text, opacity: 0.6 }}>
              This is sample card content with the card background color applied.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Theme color editor ─────────────────────────────────────────────────────

function ThemeColorEditor({
  colors,
  themeKey,
  lockedFields,
  contrastWarnings,
  disabled = false,
  onChange,
}: {
  colors: BrandingColors;
  themeKey: 'light' | 'dark';
  lockedFields: Set<string>;
  contrastWarnings: Record<string, string>;
  /**
   * Pod-QQ — Fixes #643: when `useDefaultTheme` is true, the wrapper
   * around the Tabs used to set `pointer-events-none` which blocked
   * the Light/Dark tab clicks. Now the tab switch stays live and the
   * "disabled" state is applied to the editor's color inputs via
   * this prop, mirroring the lock affordance.
   */
  disabled?: boolean;
  onChange: (key: keyof BrandingColors, value: string) => void;
}) {
  const isLocked = (k: keyof BrandingColors) =>
    disabled || lockedFields.has(`${themeKey}.${k}`);
  const warning = (k: keyof BrandingColors) => contrastWarnings[`${themeKey}.${k}`];

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <h3 className="text-sm font-semibold">General Colors</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <ColorPicker
            label="Primary"
            value={colors.primary}
            locked={isLocked('primary')}
            warning={warning('primary')}
            onChange={(v) => onChange('primary', v)}
          />
          <ColorPicker
            label="Primary Text"
            value={colors.primaryForeground}
            locked={isLocked('primaryForeground')}
            onChange={(v) => onChange('primaryForeground', v)}
          />
          <ColorPicker
            label="Page Background"
            value={colors.background}
            locked={isLocked('background')}
            onChange={(v) => onChange('background', v)}
          />
          <ColorPicker
            label="Card Background"
            value={colors.card}
            locked={isLocked('card')}
            onChange={(v) => onChange('card', v)}
          />
          <ColorPicker
            label="Text"
            value={colors.text}
            locked={isLocked('text')}
            onChange={(v) => onChange('text', v)}
          />
          <ColorPicker
            label="Pop-up Background"
            value={colors.popover}
            locked={isLocked('popover')}
            onChange={(v) => onChange('popover', v)}
          />
        </div>
      </div>

      <Separator />
      <div className="space-y-3">
        <h3 className="text-sm font-semibold">Sidebar &amp; Tabs</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <ColorPicker
            label="Background"
            value={colors.sidebarBackground}
            locked={isLocked('sidebarBackground')}
            onChange={(v) => onChange('sidebarBackground', v)}
          />
          <ColorPicker
            label="Text"
            value={colors.sidebarText}
            locked={isLocked('sidebarText')}
            onChange={(v) => onChange('sidebarText', v)}
          />
          <ColorPicker
            label="Active Background"
            value={colors.sidebarActive}
            locked={isLocked('sidebarActive')}
            onChange={(v) => onChange('sidebarActive', v)}
          />
          <ColorPicker
            label="Active Text"
            value={colors.sidebarActiveText}
            locked={isLocked('sidebarActiveText')}
            onChange={(v) => onChange('sidebarActiveText', v)}
          />
          <ColorPicker
            label="Hover Background"
            value={colors.sidebarHover}
            locked={isLocked('sidebarHover')}
            onChange={(v) => onChange('sidebarHover', v)}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

function AdminBrandingPage() {
  const { updateBranding } = useBranding();

  // Source of truth for the editor — TanStack Query against GET /branding.
  // We keep `savedSettings` (last server snapshot) and `draft` (working copy)
  // separately so Reset works deterministically.
  const { data: serverBranding, isLoading } = useBrandingQuery();
  const updateMutation = useUpdateBranding();
  const uploadMutation = useUploadBrandingAsset();
  const deleteMutation = useDeleteBrandingAsset();

  const [savedDraft, setSavedDraft] = useState<BrandingDraft>(() => defaultDraft());
  const [draft, setDraft] = useState<BrandingDraft>(() => defaultDraft());
  const [activeTheme, setActiveTheme] = useState<'light' | 'dark'>('light');
  const [showDefaultConfirm, setShowDefaultConfirm] = useState(false);
  const [showLowContrastConfirm, setShowLowContrastConfirm] = useState(false);

  // Asset ids that this session uploaded *replacements* for. After a
  // successful save we DELETE these (best-effort).
  const previousAssetIdsRef = useRef<Set<string>>(new Set());

  // Lockes + cache-bust version come from the server. The first
  // server response replaces our default draft.
  const lockedFieldsArray = serverBranding?.lockedFields ?? [];
  const lockedFields = useMemo(() => new Set(lockedFieldsArray), [lockedFieldsArray]);

  // Hydrate state when the server query resolves.
  useEffect(() => {
    if (serverBranding) {
      const next = effectiveToDraft(serverBranding);
      setSavedDraft(next);
      setDraft(next);
      previousAssetIdsRef.current.clear();
    }
  }, [serverBranding]);

  // Dirty detection — JSON-stringify is fine for a draft this small.
  const isDirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(savedDraft),
    [draft, savedDraft]
  );

  // Nav guard — beforeunload prompt when there are unsaved changes.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Modern browsers ignore the message and use a generic prompt;
      // setting returnValue is the legacy way to enable the dialog.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // ─── Updaters ───────────────────────────────────────────────────────────

  const updateColor = useCallback(
    (key: keyof BrandingColors, value: string) => {
      setDraft((prev) => ({
        ...prev,
        [activeTheme]: { ...prev[activeTheme], [key]: value },
      }));
    },
    [activeTheme]
  );

  const updateField = useCallback(
    <K extends keyof BrandingDraft>(key: K, value: BrandingDraft[K]) => {
      setDraft((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const handleSlotChange = useCallback(
    (
      slotKey: 'logoFull' | 'logoIcon' | 'favicon' | 'loginBackground',
      next: DraftAssetSlot,
      previousAssetId: string | null
    ) => {
      // If the slot is being replaced and the previous id is different,
      // queue the previous id for deletion on save.
      if (previousAssetId && previousAssetId !== next.assetId) {
        previousAssetIdsRef.current.add(previousAssetId);
      }
      setDraft((prev) => ({ ...prev, [slotKey]: next }));
    },
    []
  );

  const uploadAssetWrapper = useCallback(
    async (kind: BrandingAssetKind, file: File): Promise<{ id: string; url: string }> => {
      const result = await uploadMutation.mutateAsync({ kind, file });
      return { id: result.id, url: result.url };
    },
    [uploadMutation]
  );

  const handleReset = useCallback(() => {
    // Reset draft to the last server-known snapshot. Locked fields stay
    // locked because they come from the server snapshot.
    setDraft({ ...savedDraft });
    previousAssetIdsRef.current.clear();
  }, [savedDraft]);

  const buildWritePayload = useCallback(
    (
      d: BrandingDraft,
      saved: BrandingDraft,
      lockedSet: Set<string>,
      acknowledgeLowContrast: boolean
    ) => {
      const body: Record<string, unknown> = {};

      const setIfChanged = (
        key: string,
        nextVal: unknown,
        savedVal: unknown,
        lockKey?: string
      ) => {
        if (lockKey && lockedSet.has(lockKey)) return; // never send locked fields
        if (JSON.stringify(nextVal) !== JSON.stringify(savedVal)) {
          body[key] = nextVal;
        }
      };

      setIfChanged('useDefaultTheme', d.useDefaultTheme, saved.useDefaultTheme);
      setIfChanged('displayMode', d.displayMode, saved.displayMode, 'displayMode');
      setIfChanged('portalTitle', d.portalTitle, saved.portalTitle, 'portalTitle');
      setIfChanged(
        'logoFullAssetId',
        d.logoFull.assetId,
        saved.logoFull.assetId,
        'logoFullAssetId'
      );
      setIfChanged(
        'logoIconAssetId',
        d.logoIcon.assetId,
        saved.logoIcon.assetId,
        'logoIconAssetId'
      );
      setIfChanged('faviconAssetId', d.favicon.assetId, saved.favicon.assetId, 'faviconAssetId');
      setIfChanged(
        'loginBackgroundAssetId',
        d.loginBackground.assetId,
        saved.loginBackground.assetId,
        'loginBackgroundAssetId'
      );
      setIfChanged(
        'loginWelcomeText',
        d.loginWelcomeText,
        saved.loginWelcomeText,
        'loginWelcomeText'
      );
      setIfChanged('loginSubtitle', d.loginSubtitle, saved.loginSubtitle, 'loginSubtitle');
      setIfChanged('footerText', d.footerText, saved.footerText, 'footerText');

      // Themes — send if any sub-field changed (server diffs internally).
      if (JSON.stringify(d.light) !== JSON.stringify(saved.light)) {
        body.light = d.light;
      }
      if (JSON.stringify(d.dark) !== JSON.stringify(saved.dark)) {
        body.dark = d.dark;
      }

      if (acknowledgeLowContrast) {
        body.acknowledgeLowContrast = true;
      }
      return body;
    },
    []
  );

  const persistDraft = useCallback(
    async (acknowledgeLowContrast: boolean) => {
      const payload = buildWritePayload(draft, savedDraft, lockedFields, acknowledgeLowContrast);
      try {
        const fresh = await updateMutation.mutateAsync(payload as never);
        // Update saved-draft + propagate to the running app.
        const nextSaved = effectiveToDraft(fresh);
        setSavedDraft(nextSaved);
        setDraft(nextSaved);

        // Hot-reload — broadcast to peer tabs (W1c-08).
        updateBranding({
          useDefaultTheme: fresh.useDefaultTheme,
          displayMode: fresh.displayMode,
          portalTitle: fresh.portalTitle,
          logoUrl: fresh.logoUrl,
          logoIconUrl: fresh.logoIconUrl,
          faviconUrl: fresh.faviconUrl,
          light: fresh.light,
          dark: fresh.dark,
          loginBackgroundUrl: fresh.loginBackgroundUrl,
          loginWelcomeText: fresh.loginWelcomeText,
          loginSubtitle: fresh.loginSubtitle,
          footerText: fresh.footerText,
          effectiveVersion: fresh.effectiveVersion,
          lockedFields: fresh.lockedFields,
          source: fresh.source,
        });

        if (fresh.warnings && fresh.warnings.length > 0) {
          toast.warning('Branding saved with low-contrast warning recorded.');
        } else {
          toast.success('Branding saved and applied.');
        }

        // Best-effort cleanup of replaced assets.
        const ids = Array.from(previousAssetIdsRef.current);
        previousAssetIdsRef.current.clear();
        for (const id of ids) {
          deleteMutation.mutate(id);
        }
      } catch (err) {
        const e = err as BrandingError;
        if (e.code === 'LOCKED_FIELD') {
          const fields = e.lockedFields?.join(', ') ?? '(unknown)';
          toast.error(`Cannot save: locked field(s) — ${fields}. Reload to refresh lock state.`);
        } else if (e.code === 'INVALID_ASSET') {
          toast.error('One of the uploaded assets is no longer valid. Re-upload and try again.');
        } else if (e.status === 403) {
          toast.error('You do not have permission to change branding.');
        } else {
          toast.error(e.message ?? 'Failed to save branding.');
        }
      }
    },
    [buildWritePayload, deleteMutation, draft, lockedFields, savedDraft, updateBranding, updateMutation]
  );

  // Pre-save WCAG check (Q5 override) — if low contrast, prompt before sending.
  const lightContrast = useMemo(
    () => wcagContrast(draft.light.primary, draft.light.background),
    [draft.light.primary, draft.light.background]
  );
  const darkContrast = useMemo(
    () => wcagContrast(draft.dark.primary, draft.dark.background),
    [draft.dark.primary, draft.dark.background]
  );
  const hasLowContrast = lightContrast < WCAG_AA_NORMAL || darkContrast < WCAG_AA_NORMAL;

  const handleSave = useCallback(() => {
    if (hasLowContrast) {
      setShowLowContrastConfirm(true);
      return;
    }
    void persistDraft(false);
  }, [hasLowContrast, persistDraft]);

  const handleConfirmLowContrastSave = useCallback(() => {
    setShowLowContrastConfirm(false);
    void persistDraft(true);
  }, [persistDraft]);

  const handleDefaultToggle = useCallback(
    (checked: boolean) => {
      if (checked) {
        setShowDefaultConfirm(true);
      } else {
        updateField('useDefaultTheme', false);
      }
    },
    [updateField]
  );

  const confirmUseDefault = useCallback(() => {
    setDraft({ ...defaultDraft(), useDefaultTheme: true });
    setShowDefaultConfirm(false);
  }, []);

  // ─── Contrast warnings, by colorpicker key ─────────────────────────────

  const contrastWarnings = useMemo(() => {
    const w: Record<string, string> = {};
    if (lightContrast < WCAG_AA_NORMAL) {
      w['light.primary'] = `Contrast ${lightContrast.toFixed(2)}:1 against page background — recommended ≥${WCAG_AA_NORMAL}:1.`;
    }
    if (darkContrast < WCAG_AA_NORMAL) {
      w['dark.primary'] = `Contrast ${darkContrast.toFixed(2)}:1 against page background — recommended ≥${WCAG_AA_NORMAL}:1.`;
    }
    return w;
  }, [lightContrast, darkContrast]);

  // ─── Render ─────────────────────────────────────────────────────────────

  const currentTheme = activeTheme === 'light' ? draft.light : draft.dark;
  const portalTitleLocked = lockedFields.has('portalTitle');
  const displayModeLocked = lockedFields.has('displayMode');
  const editorBusy = updateMutation.isPending || isLoading;

  return (
    <TooltipProvider>
      <div className="p-6 space-y-6" data-testid="branding-page">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-6">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <Palette className="h-5 w-5 text-primary" />
                  <div>
                    <CardTitle>Branding Configuration</CardTitle>
                    <CardDescription>
                      Customize the look and feel of the portal.
                      {serverBranding && (
                        <span className="ml-2 text-[10px] text-muted-foreground">
                          (v{serverBranding.effectiveVersion} · {serverBranding.source})
                        </span>
                      )}
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Label htmlFor="use-default" className="text-sm text-muted-foreground">
                    Use Default
                  </Label>
                  <Switch
                    id="use-default"
                    checked={draft.useDefaultTheme}
                    onCheckedChange={handleDefaultToggle}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {hasLowContrast && (
                <Alert variant="default" className="mb-4 border-amber-500/40 bg-amber-50/40 dark:bg-amber-950/20">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertTitle>Low contrast detected</AlertTitle>
                  <AlertDescription className="text-xs">
                    {lightContrast < WCAG_AA_NORMAL && (
                      <span>
                        Light theme primary vs. background ratio is {lightContrast.toFixed(2)}:1.
                      </span>
                    )}
                    {darkContrast < WCAG_AA_NORMAL && (
                      <span>
                        {' '}
                        Dark theme primary vs. background ratio is {darkContrast.toFixed(2)}:1.
                      </span>
                    )}
                    {' '}
                    WCAG AA recommends ≥{WCAG_AA_NORMAL}:1. You can save anyway and the
                    acknowledgment is recorded in the audit log.
                  </AlertDescription>
                </Alert>
              )}

              {/* Pod-QQ — Fixes #643: previously this wrapper used
                  `pointer-events-none` when `useDefaultTheme` was true,
                  which blocked the Light/Dark tab clicks from reaching
                  the Tabs root. Tenant admins could not inspect the
                  dark-theme preview at all. The disabled visual
                  affordance (opacity-50) stays so the editor reads as
                  inactive, but tab switching itself remains live so
                  the dark-theme preview is reachable. The individual
                  color inputs are gated by their own `disabled`
                  prop driven by `draft.useDefaultTheme`. */}
              <div className={draft.useDefaultTheme ? 'opacity-50' : ''}>
                <Tabs
                  value={activeTheme}
                  onValueChange={(v) => setActiveTheme(v as 'light' | 'dark')}
                >
                  <TabsList className="mb-4">
                    <TabsTrigger value="light">Light Theme</TabsTrigger>
                    <TabsTrigger value="dark">Dark Theme</TabsTrigger>
                  </TabsList>

                  <TabsContent value="light" className="mt-0">
                    <ThemeColorEditor
                      colors={draft.light}
                      themeKey="light"
                      lockedFields={lockedFields}
                      contrastWarnings={contrastWarnings}
                      disabled={draft.useDefaultTheme}
                      onChange={(key, val) => {
                        setDraft((prev) => ({ ...prev, light: { ...prev.light, [key]: val } }));
                      }}
                    />
                  </TabsContent>
                  <TabsContent value="dark" className="mt-0">
                    <ThemeColorEditor
                      colors={draft.dark}
                      themeKey="dark"
                      lockedFields={lockedFields}
                      contrastWarnings={contrastWarnings}
                      disabled={draft.useDefaultTheme}
                      onChange={(key, val) => {
                        setDraft((prev) => ({ ...prev, dark: { ...prev.dark, [key]: val } }));
                      }}
                    />
                  </TabsContent>
                </Tabs>

                <Separator className="my-6" />
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    <ImageIcon className="h-4 w-4" />
                    Logo &amp; Assets
                  </h3>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-medium">Portal Name</Label>
                      {portalTitleLocked && <LockBadge />}
                    </div>
                    <Input
                      value={draft.portalTitle}
                      disabled={portalTitleLocked}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        updateField('portalTitle', e.target.value)
                      }
                      placeholder="ArgusAI"
                      className="h-8 text-sm"
                      maxLength={120}
                      // Issue #997 / ADMIN-BRAND-005 — stable selector
                      // for the Portal Name input. Both data-testid +
                      // aria-label for Playwright + screen readers.
                      data-testid="portal-name-input"
                      name="portalName"
                      aria-label="Portal Name"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <LogoUploadSlot
                      label="Full Logo"
                      hint="PNG/JPG/SVG, Max 2MB (Rectangular)"
                      aspectClass="min-h-[80px]"
                      slot={draft.logoFull}
                      apiKind={ASSET_SLOTS.logoFull.apiKind}
                      locked={lockedFields.has(ASSET_SLOTS.logoFull.lockedFieldName)}
                      onChange={(next, prev) => handleSlotChange('logoFull', next, prev)}
                      uploadAsset={uploadAssetWrapper}
                    />
                    <LogoUploadSlot
                      label="Logo Icon"
                      hint="PNG/JPG/SVG, Max 2MB (Square)"
                      aspectClass="min-h-[80px] aspect-square"
                      slot={draft.logoIcon}
                      apiKind={ASSET_SLOTS.logoIcon.apiKind}
                      locked={lockedFields.has(ASSET_SLOTS.logoIcon.lockedFieldName)}
                      onChange={(next, prev) => handleSlotChange('logoIcon', next, prev)}
                      uploadAsset={uploadAssetWrapper}
                    />
                    <LogoUploadSlot
                      label="Favicon"
                      hint="PNG/ICO, Max 2MB (Square)"
                      aspectClass="min-h-[80px] aspect-square"
                      slot={draft.favicon}
                      apiKind={ASSET_SLOTS.favicon.apiKind}
                      locked={lockedFields.has(ASSET_SLOTS.favicon.lockedFieldName)}
                      onChange={(next, prev) => handleSlotChange('favicon', next, prev)}
                      uploadAsset={uploadAssetWrapper}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-medium flex items-center gap-1.5">
                        <Layout className="h-3.5 w-3.5" />
                        Header Layout
                      </Label>
                      {displayModeLocked && <LockBadge />}
                    </div>
                    <Tabs
                      value={draft.displayMode}
                      onValueChange={(v) =>
                        !displayModeLocked &&
                        updateField('displayMode', v as BrandingDisplayMode)
                      }
                    >
                      <TabsList className="w-full">
                        <TabsTrigger
                          value="full"
                          disabled={displayModeLocked}
                          className="flex-1 text-xs"
                        >
                          Full Logo
                        </TabsTrigger>
                        <TabsTrigger
                          value="both"
                          disabled={displayModeLocked}
                          className="flex-1 text-xs"
                        >
                          Icon + Full
                        </TabsTrigger>
                        <TabsTrigger
                          value="icon-name"
                          disabled={displayModeLocked}
                          className="flex-1 text-xs"
                        >
                          Icon + Name
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!isDirty || editorBusy}
                  onClick={handleReset}
                >
                  <Undo2 className="h-3.5 w-3.5 mr-1.5" />
                  Reset
                </Button>
                <Button size="sm" disabled={!isDirty || editorBusy} onClick={handleSave}>
                  <Save className="h-3.5 w-3.5 mr-1.5" />
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">
                  Live Preview ({activeTheme === 'light' ? 'light' : 'dark'})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <LivePreview draft={draft} theme={currentTheme} />
              </CardContent>
            </Card>
          </div>
        </div>

        <AlertDialog open={showDefaultConfirm} onOpenChange={setShowDefaultConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Revert to Default Theme?</AlertDialogTitle>
              <AlertDialogDescription>
                This will reset all branding customizations including colors, logos, and portal name
                back to the ArgusAI defaults. This action cannot be undone unless you have saved
                your current settings.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmUseDefault}>
                Revert to Defaults
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={showLowContrastConfirm} onOpenChange={setShowLowContrastConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Save with low contrast?</AlertDialogTitle>
              <AlertDialogDescription>
                Your color choices fail WCAG 2.1 AA contrast (≥{WCAG_AA_NORMAL}:1). Text may
                be hard to read for some users. Saving anyway records your acknowledgment in
                the audit log.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmLowContrastSave}>
                Save anyway
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
}
